import { Moment } from 'moment';
import { MemberMovements, UsersWhereaboutsDay } from '@features/whereabouts/models/movements.models';
import { UNKNOWN_OPTION, WhereaboutsOption } from '@features/whereabouts/services/WhereaboutOptions';
import { DATE_FORMAT } from '@shared/utils/DateUtils';
import { shouldShowWhereaboutsInfo } from './dailyWhereaboutsUtils';
import { isOfficeDay } from './CheckinConfirmations';

export interface CellSpan {
  startIdx: number;
  span: number;
  amOption: WhereaboutsOption;
  pmOption: WhereaboutsOption;
  deskLabel?: string;
  parkingLabel?: string;
  amOfficeId?: number;
  amOptionItemLabel?: string;
  pmOptionItemLabel?: string;
  infoDates: string[];
  // Every day in the span is an office day the person checked in on
  confirmed: boolean;
}

export function whereaboutsByDate(member: MemberMovements): Map<string, UsersWhereaboutsDay> {
  const byDate = new Map<string, UsersWhereaboutsDay>();
  (member.whereabouts ?? []).forEach(w => byDate.set(w.date, w));
  return byDate;
}

// Collapse a member's week into runs of consecutive days with identical whereabouts.
export function buildSpans(
  member: MemberMovements,
  daysInWeek: Moment[],
  getOption: (statusId: number) => WhereaboutsOption,
  checkedInOn: (dateStr: string) => boolean = () => false,
): CellSpan[] {
  const byDate = whereaboutsByDate(member);
  const spans: CellSpan[] = [];
  let current: CellSpan | null = null;

  for (let i = 0; i < daysInWeek.length; i++) {
    const dateStr = daysInWeek[i].format(DATE_FORMAT);
    const dayData = byDate.get(dateStr);

    const amOption = dayData ? getOption(dayData.amStatusId) : UNKNOWN_OPTION;
    const pmOption = dayData ? getOption(dayData.pmStatusId) : UNKNOWN_OPTION;
    const deskLabel = dayData?.amDeskLabel || '';
    const parkingLabel = dayData?.amParkingSpaceLabel || '';
    const amOfficeId = dayData?.amOfficeId || 0;
    const amOptionItemLabel = dayData?.amOptionItemLabel || '';
    const pmOptionItemLabel = dayData?.pmOptionItemLabel || '';
    const wantsInfo = shouldShowWhereaboutsInfo(dayData?.dayNote, amOptionItemLabel || pmOptionItemLabel);
    const confirmed = isOfficeDay(amOption, pmOption) && checkedInOn(dateStr);

    if (
      current &&
      current.startIdx + current.span === i &&
      current.amOption.id === amOption.id &&
      current.pmOption.id === pmOption.id &&
      current.deskLabel === deskLabel &&
      current.parkingLabel === parkingLabel &&
      current.amOfficeId === amOfficeId &&
      // Same whereabouts with a different picked option/free text renders as separate spans
      current.amOptionItemLabel === amOptionItemLabel &&
      current.pmOptionItemLabel === pmOptionItemLabel &&
      current.confirmed === confirmed
    ) {
      current.span++;
      if (wantsInfo) current.infoDates.push(dateStr);
    } else {
      current = { startIdx: i, span: 1, amOption, pmOption, deskLabel, parkingLabel, amOfficeId, amOptionItemLabel, pmOptionItemLabel, infoDates: wantsInfo ? [dateStr] : [], confirmed };
      spans.push(current);
    }
  }

  return spans;
}
